import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import './Analytics.css';
import AppIcon from '../common/AppIcon';
import { getApiBaseUrl } from '../../services/api';
import { onDataChange, DATA_TYPES, shouldRefresh } from '../../services/dataEvents';

const Analytics = () => {
  const [shifts, setShifts] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const apiUrl = getApiBaseUrl();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all data from backend — each request is independent so one failure
      // does not prevent the other data from loading.
      const [shiftsRes, hospitalsRes, doctorsRes, exportRes] = await Promise.allSettled([
        axios.get(`${apiUrl}/api/processedresult`),
        axios.get(`${apiUrl}/api/hospitals`),
        axios.get(`${apiUrl}/api/doctors`),
        axios.get(`${apiUrl}/api/export?format=json`),
      ]);
      
      const shiftsData = shiftsRes.status === 'fulfilled' && Array.isArray(shiftsRes.value.data)
        ? shiftsRes.value.data : [];
      const hospitalsData = hospitalsRes.status === 'fulfilled' && Array.isArray(hospitalsRes.value.data)
        ? hospitalsRes.value.data : [];
      
      // Try /api/doctors first, then fall back to /api/export (Settings backup),
      // then fall back to shift-derived names.
      let doctorsData = [];
      if (doctorsRes.status === 'fulfilled' && Array.isArray(doctorsRes.value.data)) {
        doctorsData = doctorsRes.value.data;
      } else if (exportRes.status === 'fulfilled' && exportRes.value.data?.doctors) {
        doctorsData = Array.isArray(exportRes.value.data.doctors)
          ? exportRes.value.data.doctors : [];
      }
      
      if (shiftsRes.status === 'rejected') {
        console.error('Failed to fetch shifts for analytics:', shiftsRes.reason);
      }
      if (hospitalsRes.status === 'rejected') {
        console.error('Failed to fetch hospitals for analytics:', hospitalsRes.reason);
      }
      if (doctorsRes.status === 'rejected') {
        console.warn('/api/doctors endpoint unavailable (may not exist in this version), trying fallbacks');
      }
      
      setShifts(shiftsData);
      // Merge registered doctors with shift-derived doctors for full visibility
      const shiftDoctorNames = new Set(shiftsData.map(s => s?.name).filter(Boolean));
      const allDoctors = [...doctorsData];
      shiftDoctorNames.forEach(name => {
        if (!allDoctors.find(d => d.name === name)) {
          allDoctors.push({ name });
        }
      });
      setDoctors(allDoctors);
      setHospitals(hospitalsData);
      
    } catch (e) {
      console.error('Error loading analytics:', e);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    loadData();
    
    const interval = setInterval(loadData, 30000);
    const unsub = onDataChange((type) => {
      if (shouldRefresh(type, [DATA_TYPES.SHIFTS, DATA_TYPES.HOSPITALS, DATA_TYPES.DOCTORS, DATA_TYPES.GROUP, DATA_TYPES.ALL])) {
        loadData();
      }
    });
    return () => {
      clearInterval(interval);
      unsub();
    };
  }, [loadData]);

  // Calculate analytics data
  const calculateAnalytics = () => {
    const totalShifts = shifts.length;
    const activeDoctors = doctors.length;
    const totalHospitals = hospitals.length;
    const activeLocations = new Set(shifts.map(shift => shift.location)).size;

    // Shifts by hospital
    const shiftsByHospital = {};
    shifts.forEach(shift => {
      const location = shift.location;
      shiftsByHospital[location] = (shiftsByHospital[location] || 0) + 1;
    });

    // Shifts by doctor
    const shiftsByDoctor = {};
    shifts.forEach(shift => {
      const doctorName = shift.name;
      shiftsByDoctor[doctorName] = (shiftsByDoctor[doctorName] || 0) + 1;
    });

    // Convert to array and sort
    const topDoctors = Object.entries(shiftsByDoctor)
      .map(([name, count]) => ({ name, shifts: count }))
      .sort((a, b) => b.shifts - a.shifts)
      .slice(0, 10);

    return {
      totalShifts,
      activeDoctors,
      totalHospitals,
      activeLocations,
      shiftsByHospital,
      topDoctors
    };
  };

  const analytics = calculateAnalytics();

  const getHospitalName = (code) => {
    const hospital = hospitals.find(h => h.code === code);
    return hospital ? hospital.name : code;
  };

  if (loading) return (
    <div className="analytics-container">
      <div className="loading-spinner">Loading analytics...</div>
    </div>
  );

  if (error) return (
    <div className="analytics-container">
      <div className="error-message">Error: {error}</div>
      <button onClick={loadData} className="retry-btn">Retry</button>
    </div>
  );

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h2 className="icon-with-label">
          <AppIcon name="analytics" size={28} strokeWidth={2.25} className="app-icon--primary" />
          Analytics Dashboard
        </h2>
        <button onClick={loadData} className="refresh-btn">
          <AppIcon name="refresh" size={18} strokeWidth={2.25} />
          Refresh
        </button>
      </div>

      {/* Summary Statistics */}
      <div className="metrics-grid">
        <div className="metric-card total-shifts">
          <div className="metric-icon icon-badge icon-badge--lg">
            <AppIcon name="analytics" size={28} strokeWidth={2.25} />
          </div>
          <div className="metric-content">
            <h3>{analytics.totalShifts}</h3>
            <p>Total Shifts</p>
          </div>
        </div>
        
        <div className="metric-card active-doctors">
          <div className="metric-icon icon-badge icon-badge--lg">
            <AppIcon name="doctors" size={28} strokeWidth={2.25} />
          </div>
          <div className="metric-content">
            <h3>{analytics.activeDoctors}</h3>
            <p>Active Doctors</p>
          </div>
        </div>
        
        <div className="metric-card hospitals">
          <div className="metric-icon icon-badge icon-badge--lg">
            <AppIcon name="hospitals" size={28} strokeWidth={2.25} />
          </div>
          <div className="metric-content">
            <h3>{analytics.totalHospitals}</h3>
            <p>Hospitals</p>
          </div>
        </div>
        
        <div className="metric-card locations">
          <div className="metric-icon icon-badge icon-badge--lg">
            <AppIcon name="location" size={28} strokeWidth={2.25} />
          </div>
          <div className="metric-content">
            <h3>{analytics.activeLocations}</h3>
            <p>Active Locations</p>
          </div>
        </div>
      </div>

      {/* Analytics Tables */}
      <div className="analytics-content">
        <div className="analytics-row">
          {/* Shifts by Hospital */}
          <div className="analytics-section">
            <h3>Shifts by Hospital</h3>
            <div className="table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Hospital</th>
                    <th>Shifts</th>
                    <th>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(analytics.shiftsByHospital).map(([location, count]) => (
                    <tr key={location}>
                      <td>{getHospitalName(location)} ({location})</td>
                      <td>{count}</td>
                      <td>{analytics.totalShifts > 0 ? ((count / analytics.totalShifts) * 100).toFixed(1) : 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Top Performing Doctors */}
          <div className="analytics-section">
            <h3>Top Performing Doctors</h3>
            <div className="table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Doctor</th>
                    <th>Shifts</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.topDoctors.map((doctor, index) => (
                    <tr key={doctor.name}>
                      <td>#{index + 1}</td>
                      <td>{doctor.name}</td>
                      <td>{doctor.shifts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Registered Hospitals */}
        <div className="analytics-section">
          <h3>Registered Hospitals</h3>
          <div className="hospitals-grid">
            {hospitals.length > 0 ? (
              hospitals.map(hospital => (
                <div key={hospital._id || hospital.code} className="hospital-card">
                  <div className="hospital-code">{hospital.code}</div>
                  <div className="hospital-name">{hospital.name}</div>
                  {hospital.address && (
                    <div className="hospital-address">{hospital.address}</div>
                  )}
                  {hospital.capacity && (
                    <div className="hospital-capacity">Capacity: {hospital.capacity} beds</div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-hospitals">
                <p>No hospitals registered yet</p>
                <small>Add hospitals in the Hospitals tab</small>
              </div>
            )}
          </div>
        </div>

        {/* Shift Distribution Chart */}
        {Object.keys(analytics.shiftsByHospital).length > 0 && (
          <div className="analytics-section">
            <h3>Shift Distribution</h3>
            <div className="chart-container">
              <div className="progress-bars">
                {Object.entries(analytics.shiftsByHospital).map(([location, count], index) => (
                  <div key={location} className="progress-item">
                    <div className="progress-label">
                      <span>{getHospitalName(location)}</span>
                      <span>{count} shifts</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{
                          width: `${analytics.totalShifts > 0 ? (count / analytics.totalShifts) * 100 : 0}%`,
                          backgroundColor: `hsl(${index * 60}, 70%, 50%)`
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="last-updated">
        Last updated: {new Date().toLocaleString()}
      </div>
    </div>
  );
};

export default Analytics;


